class AdaptiveAccordion {
  constructor(element) {
    this.accordion = element;
    this.cards = this.accordion.querySelectorAll('.h-accordion__card');
    this.cardCount = this.cards.length;
    this.styleElement = null;
    this.uniqueId = `h-accordion-${Math.random().toString(36).substr(2, 9)}`;

    this.accordion.setAttribute('data-accordion-id', this.uniqueId);
    this.init();
  }

  init() {
    this.measureTextWidths();
    this.calculateDimensions();
    this.applyDimensions();
    this.accordion.classList.add('h-accordion--initialized');
    this.attachEventListeners();
    this.setMobileActiveHeight(true);
  }

  measureTextWidths() {
    const measurer = document.createElement('div');
    measurer.style.cssText = `
      position:absolute;
      left:-9999px;
      visibility:hidden;
      width:auto;
    `;
    document.body.appendChild(measurer);

    const widths = [];

    this.cards.forEach(card => {
      const text = card.querySelector('.h-accordion__text');
      if (!text) return;

      const clone = text.cloneNode(true);
      clone.style.width = 'auto';
      clone.style.maxWidth = 'none';
      clone.style.display = 'inline-block';

      measurer.appendChild(clone);
      widths.push(clone.offsetWidth);
      measurer.removeChild(clone);
    });

    document.body.removeChild(measurer);
    this.fixedTextWidth = Math.max(...widths) + 20;
  }

  calculateDimensions() {
    const accordionWidth = this.accordion.offsetWidth;
    const gap = parseFloat(getComputedStyle(this.accordion).gap) || 16;

    this.textWidth = this.fixedTextWidth;

    const activeCardWidth = this.textWidth + 128;
    const totalGaps = (this.cardCount - 1) * gap;
    const remainingWidth = accordionWidth - activeCardWidth - totalGaps;
    const inactiveCardWidth = remainingWidth / (this.cardCount - 1);

    this.activeFlexRatio = activeCardWidth / inactiveCardWidth;

    if (inactiveCardWidth < 80) {
      const newActiveWidth = accordionWidth - (80 * (this.cardCount - 1)) - totalGaps;
      this.textWidth = Math.max(300, newActiveWidth - 128);
      this.activeFlexRatio = newActiveWidth / 80;
    }
  }

  applyDimensions() {
    this.cards.forEach(card => {
      const text = card.querySelector('.h-accordion__text');
      if (!text) return;

      if (window.innerWidth > 768) {
        text.style.width = `${this.textWidth}px`;
        text.style.minWidth = `${this.textWidth}px`;
        text.style.maxWidth = `${this.textWidth}px`;
        text.style.flexShrink = '0';
        card.style.minHeight = '';
      } else {
        text.style.width = '';
        text.style.minWidth = '';
        text.style.maxWidth = '';
        text.style.flexShrink = '';
      }
    });

    if (this.styleElement) this.styleElement.remove();

    this.styleElement = document.createElement('style');
    this.styleElement.textContent = `
      @media (min-width:769px){
        [data-accordion-id="${this.uniqueId}"] .h-accordion__card--active{
          flex:${this.activeFlexRatio}!important;
        }
      }
    `;
    document.head.appendChild(this.styleElement);
  }

  getContentHeight(card) {
    const content = card.querySelector('.h-accordion__content');
    if (!content) return 0;

    const header = card.querySelector('.h-accordion__header');
    const bottomIcon = card.querySelector('.h-accordion__bottom-icon');

    const contentStyles = {
      position: content.style.position,
      visibility: content.style.visibility,
      opacity: content.style.opacity,
      transform: content.style.transform
    };

    content.style.position = 'static';
    content.style.visibility = 'visible';
    content.style.opacity = '1';
    content.style.transform = 'none';

    const height =
        content.offsetHeight;

    Object.assign(content.style, contentStyles);

    return height;
  }

  setMobileActiveHeight(force = false) {
    if (window.innerWidth > 768) return;

    this.cards.forEach(card => {
      card.style.minHeight = '';
    });

    const active = this.accordion.querySelector('.h-accordion__card--active');
    if (!active) return;

    const apply = () => {
      const height = this.getContentHeight(active);
      active.style.minHeight = `${height}px`;
    };

    requestAnimationFrame(() => {
      apply();
      if (force) setTimeout(apply, 80);
    });
  }

  attachEventListeners() {
    this.cards.forEach(card => {
      card.addEventListener('click', () => {
        if (card.classList.contains('h-accordion__card--active')) return;

        this.cards.forEach(c => {
          c.classList.remove('h-accordion__card--active');
          c.style.minHeight = '';
        });

        card.classList.add('h-accordion__card--active');
        this.setMobileActiveHeight();
      });

      card.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          card.click();
        }
      });
    });

    let resizeTimer;
    this.resizeHandler = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.measureTextWidths();
        this.calculateDimensions();
        this.applyDimensions();
        this.setMobileActiveHeight(true);
      }, 250);
    };

    window.addEventListener('resize', this.resizeHandler);
  }

  destroy() {
    if (this.styleElement) this.styleElement.remove();
    if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const accordions = document.querySelectorAll('[data-accordion]');
  window.accordionInstances = [...accordions].map(a => new AdaptiveAccordion(a));
});
